/**
 * Aba 6 "Verificação" do editor de Playbook (ADR-149 Fatia 2) — sai do
 * honest-defer: após o encerramento, a IA volta para verificar se resolveu.
 *
 * Estado derivado da MESMA `resolvePlaybookVerification` do runtime
 * (`@lume/shared`) — a UI nunca promete o que o motor não cumpre:
 *   - **Toggle mestre** (default OFF — P12 opt-in; desligado ⇒ PATCH `null`).
 *   - **Atraso pós-encerramento**: opções canônicas 1h·4h·12h·24h·48h·7d.
 *   - **5 condições** do catálogo (todas enforce-now — sem selo de pendência).
 *   - **Template aprovado**: select alimentado pelos templates `approved+enabled`
 *     reais (query GATEADA em `message-template-read`; sem a cap, valor atual
 *     read-only + nota honesta) + preview do corpo.
 *   - **Modo de operação**: H_c (IA conduz COM Gatilhos ADR-148) · H_b · H_a.
 *
 * A aba só aparece para tenant com a capability `proactive-verification`
 * (Team+) — gate de visibilidade no PlaybookEditorView (mockup: Solo não vê).
 */

import { VERIFICATION_CONDITION_CATALOG, VERIFICATION_WAIT_HOURS_OPTIONS } from '@lume/shared';
import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useTemplateList } from '../../message-templates/use-templates';
import { toVerificationDraft } from '../use-playbook-form';

import type { TemplateDto } from '../../message-templates/types';
import type { PlaybookDto } from '../types';
import type { PlaybookForm, VerificationDraft } from '../use-playbook-form';
import type { VerificationMode, VerificationWaitHours } from '@lume/shared';

const MODE_LABELS: Record<VerificationMode, string> = {
  H_c: 'H_c (IA conduz; humano só entra se cliente reportar)',
  H_b: 'H_b (IA inicia; humano assume se houver resposta)',
  H_a: 'H_a (atendente assume desde o início)',
};
const MODE_ORDER: readonly VerificationMode[] = ['H_c', 'H_b', 'H_a'];

function formatHours(h: number): string {
  return h >= 24 ? `${(h / 24).toString()}d` : `${h.toString()}h`;
}

function extractBodyPreview(template: TemplateDto | undefined): string | null {
  if (template === undefined) return null;
  const body = template.components.find((c) => c.type === 'BODY');
  return typeof body?.text === 'string' && body.text.length > 0 ? body.text : null;
}

/** Aba em modo leitura (sem `playbook-author`) — deriva snapshot da MESMA resolução do form. */
export function TabVerification({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  return <VerificationPanel draft={toVerificationDraft(playbook)} form={null} />;
}

/** Aba em modo edição (com `playbook-author`). */
export function TabVerificationEdit({ form }: { form: PlaybookForm }): JSX.Element {
  return <VerificationPanel draft={form.draft.verification} form={form} />;
}

function VerificationPanel({
  draft,
  form,
}: {
  draft: VerificationDraft;
  form: PlaybookForm | null;
}): JSX.Element {
  const editable = form !== null;

  const canReadTemplates = useCapability('message-template-read');
  const templates = useTemplateList();
  const approved = canReadTemplates
    ? templates.data.filter((t) => t.status === 'approved' && t.enabled)
    : [];
  const [previewOpen, setPreviewOpen] = useState(false);
  const selectedTemplate = approved.find((t) => t.name === draft.templateId);
  const preview = extractBodyPreview(selectedTemplate);

  const update = (next: Partial<VerificationDraft>): void => {
    if (form === null) return;
    form.set('verification', { ...form.draft.verification, ...next });
  };

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Verificação">
      <h2 className="pb-panel__title">Verificação Proativa Pós-Resolução</h2>
      <span className="pb-panel__subtitle">
        Após o encerramento, a IA retorna ao cliente para confirmar se o problema foi mesmo
        resolvido — ativada por playbook.
      </span>

      <div className="pb-trigger-row">
        <button
          type="button"
          className={'pb-check' + (draft.enabled ? ' pb-check--checked' : '')}
          role="switch"
          aria-checked={draft.enabled}
          aria-label="Habilitar verificação proativa pós-encerramento"
          disabled={!editable}
          onClick={() => {
            update({ enabled: !draft.enabled });
          }}
        >
          <span className="pb-check__box" />
          <span className="pb-check__main">
            <span className="pb-check__label">Habilitar verificação proativa pós-encerramento</span>
            <span className="pb-check__desc">
              Agenda um retorno automático ao cliente quando as condições abaixo são satisfeitas no
              encerramento.
            </span>
          </span>
        </button>
      </div>

      {draft.enabled && (
        <>
          <div className="pb-panel__divider" />

          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Atraso pós-encerramento</span>
            <div className="pb-verification-hours">
              <select
                className="pb-select pb-verification-hours__select"
                aria-label="Tempo de espera antes da verificação"
                value={draft.waitHours}
                disabled={!editable}
                onChange={(e) => {
                  update({ waitHours: Number(e.target.value) as VerificationWaitHours });
                }}
              >
                {VERIFICATION_WAIT_HOURS_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {formatHours(h)}
                  </option>
                ))}
              </select>
              <span className="pb-verification-hours__hint">
                {VERIFICATION_WAIT_HOURS_OPTIONS.map(formatHours).join(' · ')}
              </span>
            </div>
          </div>

          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Condições que disparam verificação</span>
            <div className="pb-triggers-list">
              {VERIFICATION_CONDITION_CATALOG.map((def) => {
                const checked = draft.conditions[def.id] ?? def.defaultEnabled;
                return (
                  <button
                    key={def.id}
                    type="button"
                    className={'pb-check' + (checked ? ' pb-check--checked' : '')}
                    role="switch"
                    aria-checked={checked}
                    aria-label={def.label}
                    disabled={!editable}
                    onClick={() => {
                      update({ conditions: { ...draft.conditions, [def.id]: !checked } });
                    }}
                  >
                    <span className="pb-check__box" />
                    <span className="pb-check__main">
                      <span className="pb-check__label">{def.label}</span>
                      <span className="pb-check__desc">{def.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Template aprovado pela Meta</span>
            {canReadTemplates ? (
              <>
                <div className="pb-verification-template">
                  <select
                    className="pb-select pb-verification-template__select"
                    aria-label="Template aprovado pela Meta"
                    value={draft.templateId}
                    disabled={!editable}
                    onChange={(e) => {
                      update({ templateId: e.target.value });
                      setPreviewOpen(false);
                    }}
                  >
                    <option value="">— selecionar template —</option>
                    {approved.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="pb-btn"
                    disabled={preview === null}
                    onClick={() => {
                      setPreviewOpen((v) => !v);
                    }}
                  >
                    Preview →
                  </button>
                </div>
                {draft.templateId === '' && (
                  <div className="pb-triggers-note" role="note">
                    Sem template selecionado o disparo é pulado com auditoria (
                    <code>template-missing</code>) — WhatsApp fora da janela de 24h exige template
                    aprovado.
                  </div>
                )}
                {previewOpen && preview !== null && (
                  <div className="pb-verification-preview" role="note">
                    {preview}
                  </div>
                )}
              </>
            ) : (
              <div className="pb-triggers-note" role="note">
                Template configurado:{' '}
                <code>{draft.templateId === '' ? '—' : draft.templateId}</code> — a lista de
                templates aprovados requer <code>message-template-read</code>.
              </div>
            )}
          </div>

          <div className="pb-panel__section">
            <span className="pb-panel__section-label">Modo de operação da verificação</span>
            <div className="pb-radio-row">
              {MODE_ORDER.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={'pb-radio' + (draft.mode === m ? ' pb-radio--selected' : '')}
                  role="radio"
                  aria-checked={draft.mode === m}
                  aria-label={MODE_LABELS[m]}
                  disabled={!editable}
                  onClick={() => {
                    update({ mode: m });
                  }}
                >
                  <span className="pb-radio__dot" />
                  <span className="pb-radio__main">
                    <span className="pb-radio__label">{MODE_LABELS[m]}</span>
                  </span>
                </button>
              ))}
            </div>
            {draft.mode === 'H_c' && (
              <div className="pb-triggers-note" role="note">
                Em H_c a conversa de verificação nasce conduzida pela IA — os Gatilhos de Proteção
                (aba 4, ADR-148) aplicam integralmente.
              </div>
            )}
            {draft.mode === 'H_a' && (
              <div className="pb-triggers-note" role="note">
                Em H_a o sistema não envia nada: cria uma Pendência para o atendente iniciar o
                contato manualmente via Conversa Proativa.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
